import { useCallback, useEffect, useRef } from 'react'

function pathFromHref(href: string): string | null {
  try {
    const trimmed = href.trim()
    // Bootstrap tab links use hash hrefs; these are not app route changes.
    if (trimmed === '' || trimmed === '#' || trimmed.startsWith('#')) {
      return null
    }
    if (trimmed.startsWith('/')) {
      return trimmed.split('?')[0]?.split('#')[0] ?? trimmed
    }
    const url = new URL(trimmed, window.location.href)
    return url.pathname
  } catch {
    return null
  }
}

function isLeavingChartingRoute(
  currentPath: string,
  nextPath: string,
): boolean {
  return (
    currentPath.startsWith('/charting') && !nextPath.startsWith('/charting')
  )
}

type HistoryUrl = string | URL | null | undefined

type SavedChartingLocation = {
  href: string
  state: unknown
}

const chartingPathname = (href: string): string | null => {
  try {
    return new URL(href).pathname
  } catch {
    return null
  }
}

const readHistoryPath = (url: HistoryUrl): string | null => {
  if (url == null) {
    return null
  }
  if (url instanceof URL) {
    return url.pathname
  }
  return pathFromHref(url)
}

/**
 * Blocks router navigations that leave /charting. Sidebar items call navigate(),
 * which updates history directly and never hits an anchor click.
 * Returns a function that lets a leaving navigation in the same task through, for use
 * right after the user confirms or a save completes (dirty flags may not have re-rendered).
 */
export function useChartingHistoryLeaveGuard(
  getShouldBlock: () => boolean,
  onBlocked: (targetPath: string) => void,
): () => void {
  const getShouldBlockRef = useRef(getShouldBlock)
  const onBlockedRef = useRef(onBlocked)
  const allowNextLeaveRef = useRef(false)
  getShouldBlockRef.current = getShouldBlock
  onBlockedRef.current = onBlocked

  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history)
    const originalReplaceState = window.history.replaceState.bind(
      window.history,
    )
    let saved: SavedChartingLocation = {
      href: window.location.href,
      state: window.history.state,
    }
    const remember = (): void => {
      if (!window.location.pathname.startsWith('/charting')) {
        return
      }
      saved = {
        href: window.location.href,
        state: window.history.state,
      }
    }
    remember()
    const blockIfLeaving = (url: HistoryUrl): boolean => {
      const nextPath = readHistoryPath(url)
      if (
        !nextPath ||
        !isLeavingChartingRoute(window.location.pathname, nextPath)
      ) {
        return false
      }
      if (allowNextLeaveRef.current) {
        allowNextLeaveRef.current = false
        return false
      }
      if (!getShouldBlockRef.current()) {
        return false
      }
      onBlockedRef.current(nextPath)
      return true
    }
    const pushState: History['pushState'] = (data, unused, url) => {
      if (blockIfLeaving(url)) {
        return
      }
      originalPushState(data, unused, url)
      remember()
    }
    const replaceState: History['replaceState'] = (data, unused, url) => {
      if (blockIfLeaving(url)) {
        return
      }
      originalReplaceState(data, unused, url)
      remember()
    }
    const handlePopState = (): void => {
      const nextPath = window.location.pathname
      const previousPath = chartingPathname(saved.href)
      if (
        !previousPath ||
        !getShouldBlockRef.current() ||
        !isLeavingChartingRoute(previousPath, nextPath)
      ) {
        remember()
        return
      }
      originalPushState(saved.state, '', saved.href)
      onBlockedRef.current(nextPath)
    }
    window.history.pushState = pushState
    window.history.replaceState = replaceState
    window.addEventListener('popstate', handlePopState, true)
    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.removeEventListener('popstate', handlePopState, true)
    }
  }, [])

  return useCallback((): void => {
    allowNextLeaveRef.current = true
    // Only the navigation issued in the same task may use the bypass.
    window.setTimeout(() => {
      allowNextLeaveRef.current = false
    }, 0)
  }, [])
}

/** Intercepts in-app link clicks that leave /charting (BrowserRouter has no history.block). */
export function useChartingLeaveClickGuard(
  getShouldBlock: () => boolean,
  onBlocked: (targetPath: string) => void,
): void {
  const getShouldBlockRef = useRef(getShouldBlock)
  const onBlockedRef = useRef(onBlocked)
  getShouldBlockRef.current = getShouldBlock
  onBlockedRef.current = onBlocked

  useEffect(() => {
    const handleCaptureClick = (event: MouseEvent): void => {
      if (!getShouldBlockRef.current()) {
        return
      }
      if (event.defaultPrevented || event.button !== 0) {
        return
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }
      const anchor = target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement)) {
        return
      }
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return
      }
      const href = anchor.getAttribute('href')
      if (!href) {
        return
      }
      const nextPath = pathFromHref(href)
      if (!nextPath) {
        return
      }
      const currentPath = window.location.pathname
      if (!isLeavingChartingRoute(currentPath, nextPath)) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      onBlockedRef.current(nextPath)
    }
    document.addEventListener('click', handleCaptureClick, true)
    return () => {
      document.removeEventListener('click', handleCaptureClick, true)
    }
  }, [])
}

/** Warn on tab close / refresh when charting has unsaved changes. */
export function useChartingBeforeUnload(getShouldBlock: () => boolean): void {
  const getShouldBlockRef = useRef(getShouldBlock)
  getShouldBlockRef.current = getShouldBlock

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!getShouldBlockRef.current()) {
        return
      }
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])
}
